/**
 * Strategy deployments — durable runtime registry.
 *
 * Each row is a Strategy + Instrument + Timeframe that the platform's
 * StrategyRunner is intended to run (or IS running). Operators create,
 * start, stop, delete these through the admin API; live signals are
 * read from the existing /admin/strategies/{strategy_id}/signals
 * endpoint.
 */
import { z } from 'zod';

import { del, get, post, put, type ApiResult } from './client';

export const deploymentSchema = z.object({
  id: z.string(),
  strategy_id: z.string(),
  strategy_code_name: z.string().nullable(),
  instrument_id: z.string(),
  instrument_symbol: z.string().nullable(),
  instrument_segment_id: z.string().nullable(),
  timeframe_value: z.number().int(),
  timeframe_unit: z.string(),
  status: z.enum(['stopped', 'running', 'failed']),
  candle_source_kind: z.enum(['store', 'live']),
  mode: z.enum(['paper', 'live']),
  subscription_id: z.string().nullable().optional(),   // keys the per-strategy ARM control
  // The engine's routing id (derived, e.g. candle_flip_ADANIPOWER_long_v1) —
  // signals/orders are stamped with THIS, not the catalog strategy_id.
  effective_strategy_id: z.string().nullable().optional(),
  // SCHEDULER-DRIVEN (self-driving, e.g. cpr_dmi_ss): lifecycle owned by the
  // engine's lifespan scheduler, not StrategyRunner. Start/Stop are status
  // flips; scheduler_state is the scheduler's live per-deployment view
  // (waiting-for-start / not-armed / running-session / done-for-today /
  // session-complete / session-cancelled / session-error / ...).
  scheduler_driven: z.boolean().optional().default(false),
  scheduler_state: z.string().nullable().optional(),
  scheduler_detail: z.string().nullable().optional(),
  scheduler_state_at: z.string().nullable().optional(),
  broker_account_id: z.string().nullable().optional(), // which live account it trades on
  // The account LABEL ("Bhavik (100K)"). A uuid is unreadable when several
  // deployments run one strategy — you cannot tell which account a row trades.
  broker_account_name: z.string().nullable().optional(),
  // Settlement currency of the account this trades on. The UI must never infer
  // it: the algo box is all USD, the Nirmal Bang fleet is all INR, and a
  // component that guesses is simply invisible until it meets the other one.
  base_currency: z.string().nullable().optional(),
  params_override: z.record(z.unknown()),
  started_at: z.string().nullable(),
  stopped_at: z.string().nullable(),
  last_error: z.string().nullable(),
  started_by_operator_id: z.string().nullable(),
  // Per-deployment MTM risk stop.
  max_loss_amount: z.number().nullable().optional(),
  capital_budget: z.number().nullable().optional(),
  max_loss_pct: z.number().nullable().optional(),
  effective_loss_limit: z.number().nullable().optional(),
  risk_halted_at: z.string().nullable().optional(),
  risk_halt_reason: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const deploymentListSchema = z.array(deploymentSchema);
export type StrategyDeployment = z.infer<typeof deploymentSchema>;

export interface DeploymentListQuery {
  status?: 'stopped' | 'running' | 'failed';
  /** Substring match on strategy id or account display name. */
  q?: string;
  /** OMIT for every row (the historical behaviour). Present = one page. */
  limit?: number;
  offset?: number;
}

export const listDeployments = (
  filter: DeploymentListQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<StrategyDeployment[]>> =>
  get('/admin/strategy-deployments', deploymentListSchema, {
    signal,
    query: {
      status: filter.status,
      q: filter.q,
      limit: filter.limit,
      offset: filter.offset,
    },
  });

/** Paged envelope read off `X-Total-Count` — an honest "X of Y" footer. */
export interface PagedDeployments {
  items: StrategyDeployment[];
  total: number;
}

export const listDeploymentsPaged = async (
  filter: DeploymentListQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<PagedDeployments>> => {
  const r = await listDeployments(filter, signal);
  const headerTotal = r.meta.headers?.['x-total-count'];
  const total =
    headerTotal !== undefined && headerTotal !== '' ? Number(headerTotal) : r.data.length;
  return {
    data: { items: r.data, total: Number.isFinite(total) ? total : r.data.length },
    meta: r.meta,
  };
};

export const getDeployment = (
  id: string,
  signal?: AbortSignal,
): Promise<ApiResult<StrategyDeployment>> =>
  get(
    `/admin/strategy-deployments/${encodeURIComponent(id)}`,
    deploymentSchema,
    { signal },
  );

export interface CreateDeploymentBody {
  strategy_id: string;
  instrument_id: string;
  timeframe_value: number;
  timeframe_unit: 's' | 'm' | 'h' | 'd' | 'w';
  candle_source_kind?: 'store' | 'live';
  mode?: 'paper' | 'live';
  broker_account_id?: string;   // required for mode='live'
  params_override?: Record<string, unknown>;
  max_loss_amount?: number;     // absolute MTM loss limit
  capital_budget?: number;      // % base
  max_loss_pct?: number;        // % of budget (needs capital_budget)
  reason?: string;
}

export const createDeployment = (body: CreateDeploymentBody) =>
  post('/admin/strategy-deployments', deploymentSchema, body);

export interface RiskLimitBody {
  max_loss_amount?: number | null;
  capital_budget?: number | null;
  max_loss_pct?: number | null;
  reason?: string;
}

export const setRiskLimit = (id: string, body: RiskLimitBody) =>
  put(
    `/admin/strategy-deployments/${encodeURIComponent(id)}/risk-limit`,
    deploymentSchema,
    { reason: 'set risk limit', ...body },
  );

export const clearRiskHalt = (id: string) =>
  post(
    `/admin/strategy-deployments/${encodeURIComponent(id)}/clear-risk-halt`,
    deploymentSchema,
    {},
  );

export const startDeployment = (id: string, reason?: string) =>
  post(
    `/admin/strategy-deployments/${encodeURIComponent(id)}/start`,
    deploymentSchema,
    { reason: reason ?? 'manual start' },
  );

export const stopDeployment = (id: string, reason?: string) =>
  post(
    `/admin/strategy-deployments/${encodeURIComponent(id)}/stop`,
    deploymentSchema,
    { reason: reason ?? 'manual stop' },
  );

export const deleteDeployment = (id: string) =>
  del(`/admin/strategy-deployments/${encodeURIComponent(id)}`);

// ── Activity (audit history) ────────────────────────────────────────────
export const auditEventSchema = z.object({
  id: z.string(),
  operator_id: z.string(),
  operator_email: z.string().nullable(),
  operator_display_name: z.string().nullable(),
  target_kind: z.string(),
  target_id: z.string(),
  action: z.string(),
  reason: z.string(),
  before_state: z.record(z.unknown()).nullable(),
  after_state: z.record(z.unknown()).nullable(),
  request_id: z.string().nullable(),
  at: z.string(),
});
export const auditEventListSchema = z.array(auditEventSchema);
export type DeploymentAuditEvent = z.infer<typeof auditEventSchema>;

export const getDeploymentActivity = (
  id: string,
  signal?: AbortSignal,
): Promise<ApiResult<DeploymentAuditEvent[]>> =>
  get(
    `/admin/strategy-deployments/${encodeURIComponent(id)}/activity`,
    auditEventListSchema,
    { signal },
  );

// ── Strategy signals (already wired in api/routes/strategy_signals.py) ──
export const strategySignalSchema = z.object({
  id: z.string(),
  strategy_id: z.string(),
  instrument_id: z.string().nullable().optional(),
  instrument_symbol: z.string().nullable(),
  emitted_at: z.string(),
  // Bar OHLC + close-time are only captured by the streaming host, not the
  // executing (deployment) path — null for deployment signals.
  bar_close_time: z.string().nullable().optional(),
  side: z.enum(['buy', 'sell', 'flat']),
  size_unit: z.number(),
  sizing_mode: z.string(),
  reason: z.string(),
  bar_open: z.number().nullable().optional(),
  bar_high: z.number().nullable().optional(),
  bar_low: z.number().nullable().optional(),
  bar_close: z.number().nullable().optional(),
});
export const strategySignalListSchema = z.array(strategySignalSchema);
export type StrategySignal = z.infer<typeof strategySignalSchema>;

export const getStrategySignals = (
  strategy_id: string,
  query: { limit?: number; since?: string } = {},
  signal?: AbortSignal,
): Promise<ApiResult<StrategySignal[]>> =>
  get(
    `/admin/strategies/${encodeURIComponent(strategy_id)}/signals`,
    strategySignalListSchema,
    {
      signal,
      query: {
        limit: query.limit,
        since: query.since,
      },
    },
  );

// ── Per-deployment execution: P&L roll-up + trades + orders ──
// realized_pnl is NET of carry charges (gross - carry); gross_pnl = realized +
// charges. The frontend must NOT subtract charges again.
export const tradeRowSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  side: z.string(),
  quantity: z.number(),
  entry_time: z.string(),
  entry_price: z.number(),
  exit_time: z.string().nullable().optional(),
  exit_price: z.number().nullable().optional(),
  is_open: z.boolean(),
  realized_pnl: z.number(),
  charges: z.number(),
  entry_signal: z.string(),
  exit_signal: z.string(),
});
export type TradeRow = z.infer<typeof tradeRowSchema>;

export const orderRowSchema = z.object({
  id: z.number(),
  symbol: z.string(),
  side: z.string(),
  quantity: z.number(),
  order_type: z.string(),
  limit_price: z.number().nullable().optional(),
  status: z.string(),
  avg_fill_price: z.number().nullable().optional(),
  filled_qty: z.number(),
  broker_order_id: z.string().nullable().optional(),
  client_order_id: z.string().nullable().optional(),
  created_at: z.string(),
});
export type OrderRow = z.infer<typeof orderRowSchema>;

export const deploymentExecutionSchema = z.object({
  deployment_id: z.string(),
  subscription_id: z.string().nullable().optional(),
  pnl: z.object({
    realized_pnl: z.number(),
    charges: z.number(),
    gross_pnl: z.number(),
    total_trades: z.number(),
    closed_trades: z.number(),
    open_trades: z.number(),
    wins: z.number(),
    losses: z.number(),
    win_rate: z.number().nullable().optional(),
  }),
  trades: z.array(tradeRowSchema),
  orders: z.array(orderRowSchema),
  // true when the displayed trade/order ROW LISTS were capped (the pnl summary is
  // ALWAYS computed over the full set regardless).
  truncated: z.boolean().optional().default(false),
});
export type DeploymentExecution = z.infer<typeof deploymentExecutionSchema>;

export const getDeploymentExecution = (
  id: string,
  signal?: AbortSignal,
): Promise<ApiResult<DeploymentExecution>> =>
  get(
    `/admin/strategy-deployments/${encodeURIComponent(id)}/execution`,
    deploymentExecutionSchema,
    { signal },
  );
